/**
 * Given a list of tabs, the width of the container we wish them to fit into,
 * and the widths of each tab, partition the list of tabs into a set of
 * "visible" tabs (that can be shown in the tab list) and "overflow" tabs (which
 * are tucked away in a side menu).
 *
 * A key insight here is that the selected tab (if any) _must_ go into the
 * list of visible tabs, even if it would normally overflow into the menu. This
 * means that some otherwise visible tabs must be tucked away into the side menu
 * to make room for the selected tab.
 *
 * Based on this assumption, we can try to place each tab into the container,
 * pretending that the selected tab is already placed. Once we actually
 * encounter the selected tab, we place it, and then subsequent tabs don't
 * have to consider the space taken up by the selected tab.
 *
 * Once all the space has been used up and the next tab won't fit, we say
 * we have `overflowed` and all subsequent tabs go into the side menu (unless
 * we see the selected tab, in which case we make a special case to put it in
 * the visible tabs list).
 */
export function computeOverflowTabs(
  tabs: HTMLAhaTabElement[],
  tabWidths: number[],
  containerWidth: number,
): {
  selectedTab: HTMLAhaTabElement | undefined;
  visibleTabs: HTMLAhaTabElement[];
  overflowTabs: HTMLAhaTabElement[];
} {
  const visibleTabs: HTMLAhaTabElement[] = [];
  const overflowTabs: HTMLAhaTabElement[] = [];
  let widthSoFar = 0;
  let overflowing = false;

  const selectedTab = tabs.find(tab => tab.selected);
  let selectedTabIndex = tabs.indexOf(selectedTab!);
  let selectedTabWidth = tabWidths[selectedTabIndex] || 0;

  tabs.forEach((tab, index) => {
    const tabWidth = tabWidths[index];

    const tabIsSelected = tab === selectedTab;
    const tabFits = widthSoFar + tabWidth + selectedTabWidth <= containerWidth;

    if (tabIsSelected) {
      // Since we've encountered the selected tab, we can remove the empty space
      // we've been reserving for it when we were placing all the previous tabs.
      selectedTabWidth = 0;
    }

    if (tabIsSelected || (!overflowing && tabFits)) {
      visibleTabs.push(tab);
      widthSoFar += tabWidth;
    } else {
      overflowTabs.push(tab);
      overflowing = true;
    }
  });

  return { selectedTab, visibleTabs, overflowTabs };
}
